import { useAuthStore } from '@/stores/useAuthStore'
import { computed, ref } from 'vue'

export type NavigationItem = {
    title: string
    path?: string
    name?: string
    query?: Record<string, string>
    roles: string[]
    beta?: boolean
}

export type Navigation = {
    title: string
    roles: string[]
    children: NavigationItem[]
}

export default function useNavigations() {
    const storeAuth = useAuthStore()

    const isLive = process.env.VUE_APP_ENV === 'master'

    const navigationsList = ref<Navigation[]>([
        {
            title: 'Order',
            roles: ['Web_Nav_Order'],
            children: [
                { title: 'Sales Order', path: '/order/index', roles: ['Web_Nav_Order_SalesOrder'] },
                { title: 'Purchase Orders', name: 'order.purchase-orders', roles: ['Web_Nav_Order_PurchaseOrders'] },
                {
                    title: 'Failed Import',
                    name: 'order.failed-imports',
                    roles: ['Web_Nav_Order_FailedImports'],
                },
            ],
        },
        {
            title: 'Customer Service',
            roles: ['Web_Nav_CustomerService'],
            children: [
                {
                    title: 'Customer Search',
                    name: 'customer-service.customer-search',
                    roles: ['Web_Nav_CustomerService_CustomerSearch'],
                },
                {
                    title: 'Credit Notes',
                    name: 'customer-service.credit-notes',
                    roles: ['Web_Nav_CustomerService_CreditNotes'],
                },
                {
                    title: 'RMA',
                    name: 'customer-service.rma',
                    roles: ['Web_Nav_CustomerService_Rma'],
                },
            ],
        },
        {
            title: 'Inventory',
            roles: ['Web_Nav_Inventory'],
            children: [
                {
                    title: 'Inventory Listings',
                    path: '/materiallistings/index',
                    roles: ['Web_Nav_Inventory_InventoryListings'],
                },
                { title: 'Manage Inventory', path: '/inventory/index', roles: ['Web_Nav_Inventory_ManageInventory'] },
                { title: 'Import Materials', path: '/material/import', roles: ['Web_Nav_Inventory_ImportMaterials'] },
                {
                    title: 'Import Bundles',
                    name: 'import.data.sheets',
                    query: { importType: 'bundles' },
                    roles: ['Web_Nav_Inventory_BundleMaterials'],
                },
                {
                    title: 'Search Material Group',
                    path: '/materialgroup/index',
                    roles: ['Web_Nav_Inventory_SearchMaterialGroup'],
                },
                {
                    title: 'Portfolio Management',
                    name: 'inventory.portfolio.management.portfolio',
                    roles: ['Web_Nav_Admin_portfolioManagement'],
                },
                {
                    title: 'Tax Classes',
                    name: 'inventory.tax-classes',
                    roles: ['Web_Nav_Channel_TaxClasses'],
                },
            ],
        },
        {
            title: 'Report',
            roles: ['Web_Nav_Reporting'],
            children: [
                { title: 'Finance Report', name: 'report.finance-report', roles: ['Web_Nav_Reporting_FinanceReport'] },
                {
                    title: 'Business Intelligence',
                    name: 'report.business-intelligence',
                    roles: ['COBI_Access'],
                },
            ],
        },
        {
            title: 'Analysis',
            roles: ['Web_Nav_Analysis'],
            children: [
                {
                    title: 'Amazon Price Analysis',
                    path: '/productpriceanalysis/index',
                    roles: ['Web_Nav_Analysis_AmazonPriceAnalysis'],
                },
                {
                    title: 'Active Price Alerts',
                    path: '/productpriceanalysis/activepriceanalysis',
                    roles: ['Web_Nav_Analysis_ActivePriceAlerts'],
                },
                {
                    title: 'Invoices Validation',
                    path: '/invoicesvalidation/index',
                    roles: ['Web_Nav_Invoices_Validation'],
                },
                {
                    title: 'Margin Optimiser',
                    name: 'analysis.amazon.repricer.sisense',
                    roles: ['Web_Nav_Repricer'],
                },
            ],
        },
        {
            title: 'Channel',
            roles: ['Web_Nav_Channel'],
            children: [
                {
                    title: 'Sales Channels',
                    path: '/saleschannel/saleschannelsdashboard',
                    roles: ['Web_Nav_Channel_Sales_Channels'],
                },
                {
                    title: 'Channel Profiles',
                    name: 'channel.channel-profiles',
                    roles: ['Web_Nav_Channel_ChannelProfiles'],
                },
                { title: 'Clients', name: 'channel.clients', roles: ['Web_Nav_Channel_Clients'] },
                {
                    title: 'Import Channel Profile Mappings',
                    name: 'import.data.sheets',
                    query: { importType: 'channelProfileMappings' },
                    roles: ['Web_Nav_Channel_ImportChannelProfileMappings'],
                },
                {
                    title: 'Shipping Cost Category',
                    path: '/shippingcostcategory/index',
                    roles: ['Web_Nav_Channel_ShippingCostCategory'],
                },
                {
                    title: 'Sales Channel Category',
                    path: '/saleschannelcategory/index',
                    roles: ['Web_Nav_Channel_SalesChannelCategory'],
                },
                {
                    title: 'ForSure Clients',
                    name: 'channel.forsure-clients',
                    roles: ['Web_Nav_Channel_ForSureManagement'],
                },
            ],
        },
        {
            title: 'Admin',
            roles: ['Web_Nav_Admin'],
            children: [
                { title: 'Layout', name: 'admin.layouts', roles: ['Web_Nav_Admin_Layout'] },
                { title: 'Email Template', path: '/emailtemplate/search', roles: ['Web_Nav_Admin_EmailTemplate'] },
                {
                    title: 'Product Road Map',
                    name: 'admin.product-road-map',
                    roles: ['Web_Nav_Admin_ProductRoadMap'],
                },
                {
                    title: 'Job History Logs',
                    name: 'admin.job-history-logs',
                    roles: ['Web_Nav_Admin_JobHistoryLogs'],
                },
                {
                    title: 'Supplier Messages',
                    name: 'admin.supplier-messages',
                    roles: ['Web_Nav_Support_MessageHistoryLogs'],
                },
                { title: 'Forex Rates', name: 'admin.forex.rates', roles: ['Web_Nav_Admin_ForExRates'] },
                { title: 'RRRR', name: 'admin.remittances', roles: ['Web_Nav_RemittanceManagement'] },
                { title: 'Api Logs', name: 'admin.system.log.api.logs', roles: ['Web_Nav_Admin_apilogs'] },
                { title: 'User Management', name: 'admin.users', roles: ['Web_Nav_Admin_usermgt'] },
                // {
                //     title: 'API Keys Management',
                //     name: 'admin.api.keys.management',
                //     roles: ['Web_Nav_Admin_usermgt'],
                // },
                {
                    title: 'Payment Methods',
                    name: 'admin.channel.account.payment.methods',
                    roles: ['Web_Nav_Admin_paymentmethods'],
                },
                { title: 'Courier Mappings', name: 'admin.courier.mappings', roles: ['Web_Nav_Admin_couriermappings'] },
                { title: 'System Events', name: 'admin.system.event', roles: ['Web_Nav_Admin_systemevents'] },
                {
                    title: 'Configuration',
                    name: 'admin.configuration.settings',
                    roles: ['Web_Nav_Admin_configuration'],
                },
                {
                    title: 'Braintree Transactions',
                    name: 'admin.braintree-transactions',
                    roles: ['Web_Nav_Invoices_Transaction'],
                },
                {
                    title: 'SP Collector',
                    name: 'analysis.sp.collector',
                    roles: ['Web_Nav_Admin_apilogs'],
                },
                {
                    title: 'Manual Journal Entries',
                    name: 'manual.journal.entries',
                    roles: ['Web_Nav_Manual_Journal_Entries'],
                },
                { title: 'Release Manager', name: 'admin.release-manager', roles: ['Web_Nav_Admin_ReleaseManager'] },
                {
                    title: 'System Events Monitor',
                    name: 'admin.system-events-monitor',
                    roles: ['Web_Nav_Admin_systemevents'],
                },
            ],
        },
    ])

    const navigations = computed(() => {
        return navigationsList.value.reduce((acc: Navigation[], nav: Navigation) => {
            if (nav.roles.length === 0 || storeAuth.isUserHasRole(nav.roles)) {
                acc.push({
                    title: nav.title,
                    roles: nav.roles,
                    children: nav.children.filter((child) => {
                        if (child.beta && isLive) {
                            return false
                        }
                        return child.roles.length === 0 || storeAuth.isUserHasRole(child.roles)
                    }),
                })
            }
            return acc
        }, [])
    })

    return { navigations }
}
